import React, { useCallback } from 'react';
import { MultiSelectDropdown, debounce, useToasts } from 'freemium-ui';
import { useTranslation } from 'react-i18next';
import { useAddTagMutation, useGetTags } from 'routes/screens/response/hooks';
import { isTagValid } from 'routes/utils';

type SearchTagsProps = {
  label?: string;
  placeholder?: string;
  selectedItems: any[];
  setSelectedItems: (data: any[]) => void;
};

type TagType = {
  id?: any;
  name: string;
  deleted?: boolean;
};

const SearchTagsAction = ({
  label,
  placeholder,
  selectedItems,
  setSelectedItems,
  ...rest
}: SearchTagsProps) => {
  const [tagName, setTagName] = React.useState('');
  const [, setToast] = useToasts();
  const { t } = useTranslation();

  const {
    data: tagsData = [],
    refetch: refetchTags,
    isLoading: isTagsLoading,
    isFetching: isTagsFetching,
  } = useGetTags({ searchParam: tagName });

  const handleInputChange = (inputVal: string) => {
    if (inputVal.length) {
      debouncedSearch();
      setTagName(inputVal);
    }
  };

  const { mutate: addTag } = useAddTagMutation({
    callback: (isSuccess: boolean, response: any) => {
      if (isSuccess) {
        setToast({
          delay: 3000,
          type: 'success',
          text: `${t('forms.toast.success.tag_created')}`,
        });
        selectedItems.push(response);
        setSelectedItems(selectedItems);
      } else {
        setToast({
          delay: 3000,
          type: 'error',
          text: `${t('forms.toast.error.tag_creation_failed')}`,
        });
      }
    },
  });

  const createTagIfNotExists = (payload: { type: string; value: any[] }) => {
    const items = payload?.value;
    // when removing tags, items will be undefined when removing last item. So adding if check
    if (items) {
      refetchTags();
      const tagName: string =
        items && items.length > 0 ? items[items.length - 1].name : null;
      if (tagName && tagName.length > 2 && isTagValid(tagName)) {
        // the OR condition is while removing the selected tags, it will create tag. SO to prevent it
        const isTagExists: boolean =
          tagsData?.accountTags.some(
            (item: TagType) => item.name === tagName,
          ) || selectedItems.some((item) => item.name === tagName);
        if (!isTagExists) {
          addTag({
            payload: {
              name: tagName,
            },
            responseId: '',
          });
        }
      }
    }
  };

  const debouncedSearch = useCallback(
    debounce(() => {
      refetchTags();
    }, 500),
    [],
  );

  const updateValues = (items: any[]) => {
    const payload = {
      type: 'MULTISELECT',
      value: items,
    };
    setSelectedItems(items);
    createTagIfNotExists(payload);
  };

  return (
    <MultiSelectDropdown
      name={label || ''}
      items={tagsData?.accountTags || []}
      selectedItem={selectedItems}
      onChange={updateValues}
      isMenuLoading={isTagsFetching || isTagsLoading}
      customValues
      itemLabelIdentifier="name"
      itemValueIdentifier="name"
      label={label}
      placeholder={selectedItems.length ? '' : placeholder}
      handleInputChange={handleInputChange}
      isDynamicMenu
      {...rest}
    />
  );
};

export default SearchTagsAction;
